/**
 * Formats a date in a user-friendly way
 */
export function formatDateForError(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  if (timezone) {
    options.timeZone = timezone;
  }
  return date.toLocaleString("en-GB", options);
}

/**
 * Validates that price points provide continuous coverage from when booking becomes available
 * until booking closes. Gaps before the first price point becomes available are allowed.
 */
export function validatePricePointCoverage(
  pricePoints: Array<{
    availableFrom?: Date | null;
    availableUntil?: Date | null;
  }>,
  bookingClosesAt: Date | null,
  startsAt: Date,
  isPublished: boolean,
  timezone?: string,
): { valid: boolean; error?: string } {
  // If event is not published, skip validation (can be fixed before publishing)
  if (!isPublished) {
    return { valid: true };
  }

  if (pricePoints.length === 0) {
    return {
      valid: false,
      error: "At least one price point is required for published events",
    };
  }

  // Calculate the booking period
  const bookingEnd = bookingClosesAt || startsAt;

  // Calculate effective availability windows for each price point
  // Track whether boundaries are explicitly set (not null) to allow intentional gaps
  const windows = pricePoints.map((pp) => {
    const from = pp.availableFrom || new Date(0); // Available from beginning of time if null
    const until = pp.availableUntil || bookingEnd; // Available until booking closes if null
    const hasExplicitFrom = !!pp.availableFrom;
    const hasExplicitUntil = !!pp.availableUntil;

    return { from, until, hasExplicitFrom, hasExplicitUntil };
  });

  // Find when booking actually becomes available (earliest availableFrom)
  // Only consider windows that start before booking ends
  const relevantWindows = windows.filter((w) => w.from < bookingEnd);

  if (relevantWindows.length === 0) {
    return {
      valid: false,
      error:
        "No price points are available before booking closes. At least one price point must become available before booking ends.",
    };
  }

  // Find the earliest availableFrom (when booking becomes available)
  const bookingStarts = new Date(
    Math.min(...relevantWindows.map((w) => w.from.getTime())),
  );

  // Sort windows by start time within the booking period
  const sortedWindows = relevantWindows
    .filter((w) => w.until > bookingStarts) // Only windows that overlap with booking period
    .sort((a, b) => a.from.getTime() - b.from.getTime());

  // Check for gaps in coverage from bookingStarts to bookingEnd
  let currentCoverageEnd = bookingStarts;
  let previousWindow: (typeof sortedWindows)[0] | null = null;

  for (const window of sortedWindows) {
    // Check if this window overlaps with the booking period
    const windowStart =
      window.from > bookingStarts ? window.from : bookingStarts;
    const windowEnd = window.until < bookingEnd ? window.until : bookingEnd;

    // If window starts at or before current coverage ends, extend coverage
    // Note: If a price point ends at time T, the next can start at T (same moment) for continuous coverage
    if (windowStart <= currentCoverageEnd) {
      // Window overlaps or continues coverage - extend it
      if (windowEnd > currentCoverageEnd) {
        currentCoverageEnd = windowEnd;
      }
      previousWindow = window;
    } else {
      // Gap found
      // Gaps are allowed in two cases:
      // 1. Gap before the first price point (when previousWindow is null)
      // 2. Intentional gap when both boundaries are explicitly set (previous has explicit Until, current has explicit From)
      //    and they are within 1 second of each other
      const isAllowedGap =
        !previousWindow ||
        (previousWindow.hasExplicitUntil &&
          window.hasExplicitFrom &&
          Math.abs(window.from.getTime() - previousWindow.until.getTime()) <=
            1000);

      if (!isAllowedGap) {
        // Gap found that's not allowed
        return {
          valid: false,
          error: `Price point coverage has a gap from ${formatDateForError(currentCoverageEnd, timezone)} to ${formatDateForError(windowStart, timezone)}. Once booking becomes available, price points must provide continuous coverage until booking closes.`,
        };
      }

      // Allowed gap - continue coverage from this window (skip over the gap)
      if (windowEnd > currentCoverageEnd) {
        currentCoverageEnd = windowEnd;
      }
      previousWindow = window;
    }
  }

  // Check if coverage extends to booking end
  if (currentCoverageEnd < bookingEnd) {
    return {
      valid: false,
      error: `Price point coverage ends at ${formatDateForError(currentCoverageEnd, timezone)}, but booking closes at ${formatDateForError(bookingEnd, timezone)}. At least one price point must remain available until booking closes.`,
    };
  }

  return { valid: true };
}

/**
 * Gets price points that are currently available for booking
 */
export function getAvailablePricePoints(
  pricePoints: Array<{
    id: string;
    availableFrom: Date | null;
    availableUntil: Date | null;
  }>,
  bookingClosesAt: Date | null,
  startsAt: Date,
) {
  const now = new Date();
  const bookingEnd = bookingClosesAt || startsAt;

  // Check if booking is still open
  if (now >= bookingEnd) {
    return []; // Booking closed
  }

  // Filter price points that are currently available
  return pricePoints.filter((pp) => {
    const availableFrom = pp.availableFrom || new Date(0);
    const availableUntil = pp.availableUntil || bookingEnd;

    return now >= availableFrom && now <= availableUntil;
  });
}

/**
 * Checks if booking is still open for an event
 */
export function isBookingOpen(
  bookingClosesAt: Date | null,
  startsAt: Date,
): boolean {
  const now = new Date();
  const cutoff = bookingClosesAt || startsAt;
  return now < cutoff;
}
